/**
 * 상태 한국어 변환 유틸리티
 *
 * 플레이리스트, 트랙, 로그 등의 상태값을 한국어로 변환합니다.
 * 모든 컴포넌트에서 일관된 상태 표시를 위해 사용합니다.
 *
 * @module lib/utils/statusUtils
 */

import { createElement, type ReactNode } from 'react';
import {
  CheckCircle,
  Zap,
  Clock,
  XCircle,
  AlertCircle,
  Megaphone,
  List,
  Music,
  FileCheck,
  Pause,
  Ban,
} from 'lucide-react';

// ============================================================================
// 상태 정보 타입
// ============================================================================

/** AppBadge 컴포넌트의 variant 타입 (AppBadge.tsx의 BadgeVariant와 동일) */
export type BadgeVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'neutral';

/** 상태 아이콘 크기 옵션 */
export type IconSize = 'sm' | 'md' | 'lg';

const ICON_SIZE_CLASSES: Record<IconSize, string> = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

// ============================================================================
// 상태 매핑
// ============================================================================

/**
 * 플레이리스트/트랙 상태 한국어 매핑
 */
export const STATUS_LABELS: Record<string, string> = {
  // 공통 상태
  completed: '완료',
  failed: '실패',
  pending: '대기 중',
  generating: '메타데이터 생성 중',
  published: '발행됨',

  // 음악 생성 관련 상태
  music_generating: '음악 생성 중',
  metadata_ready: '메타데이터 완료',

  // 추가 상태
  paused: '일시정지',
  cancelled: '취소됨',

  // 로그/작업 상태
  running: '진행 중',
  in_progress: '진행 중',
  queued: '대기열',
  skipped: '건너뜀',

  // 트랙 버전 상태
  selected: '선택됨',
  included: '포함됨',

  // 기본값
  default: '알 수 없음',
};

// ============================================================================
// 변환 함수
// ============================================================================

/**
 * 상태값을 한국어 라벨로 변환
 *
 * @param status - 영문 상태값 (completed, failed, pending 등)
 * @returns 한국어 상태 라벨
 *
 * @example
 * getStatusLabel('completed') // '완료'
 * getStatusLabel('failed') // '실패'
 * getStatusLabel('generating') // '생성 중'
 */
export function getStatusLabel(status: string | undefined | null): string {
  if (!status) { return STATUS_LABELS.default; }
  return STATUS_LABELS[status.toLowerCase()] || status;
}

/**
 * 로그 상태값을 한국어 라벨로 변환
 * getStatusLabel과 동일하지만 로그 컨텍스트에서 사용
 *
 * @param status - 영문 상태값 (running, queued, cancelled 등)
 * @returns 한국어 상태 라벨
 *
 * @example
 * getLogStatusLabel('running') // '진행 중'
 * getLogStatusLabel('queued') // '대기열'
 */
export function getLogStatusLabel(status: string | undefined | null): string {
  return getStatusLabel(status);
}

// ============================================================================
// 상태 스타일 정보
// ============================================================================

/**
 * 상태별 스타일 정보 타입
 */
export interface StatusInfo {
  label: string;
  color?: string;
  textColor?: string;
}

/**
 * 상태별 스타일 및 라벨 매핑
 */
export const STATUS_STYLES: Record<string, { label: string; color: string; textColor: string }> = {
  completed: { label: '완료', color: 'bg-green-100', textColor: 'text-green-800' },
  failed: { label: '실패', color: 'bg-red-100', textColor: 'text-red-800' },
  pending: { label: '대기 중', color: 'bg-yellow-100', textColor: 'text-yellow-800' },
  generating: { label: '메타데이터 생성 중', color: 'bg-blue-100', textColor: 'text-blue-800' },
  music_generating: { label: '음악 생성 중', color: 'bg-purple-100', textColor: 'text-purple-800' },
  metadata_ready: { label: '메타데이터 완료', color: 'bg-indigo-100', textColor: 'text-indigo-800' },
  running: { label: '진행 중', color: 'bg-blue-100', textColor: 'text-blue-800' },
  in_progress: { label: '진행 중', color: 'bg-blue-100', textColor: 'text-blue-800' },
  queued: { label: '대기열', color: 'bg-gray-100', textColor: 'text-gray-800' },
  cancelled: { label: '취소됨', color: 'bg-gray-100', textColor: 'text-gray-800' },
  paused: { label: '일시정지', color: 'bg-yellow-100', textColor: 'text-yellow-800' },
  published: { label: '발행됨', color: 'bg-green-100', textColor: 'text-green-800' },
};

/**
 * 상태에 따른 라벨과 스타일 정보를 반환
 *
 * @param status - 영문 상태값
 * @returns 라벨과 스타일 정보를 포함한 객체
 *
 * @example
 * getStatusInfo('completed') // { label: '완료', color: 'bg-green-100', textColor: 'text-green-800' }
 * getStatusInfo('custom') // { label: 'custom' }
 */
export function getStatusInfo(status: string | undefined | null): StatusInfo {
  const label = getStatusLabel(status);
  const normalizedStatus = status?.toLowerCase();

  if (!normalizedStatus || !STATUS_STYLES[normalizedStatus]) {
    return { label };
  }

  const style = STATUS_STYLES[normalizedStatus];
  return {
    label,
    color: style.color,
    textColor: style.textColor,
  };
}

// ============================================================================
// 상태 배지 (AppBadge) 관련 함수
// ============================================================================

/**
 * 상태별 AppBadge variant 매핑
 */
const STATUS_BADGE_VARIANTS: Record<string, BadgeVariant> = {
  completed: 'success',
  generating: 'secondary',
  music_generating: 'primary',
  metadata_ready: 'secondary',
  paused: 'warning',
  cancelled: 'neutral',
  running: 'secondary',
  in_progress: 'secondary',
  failed: 'error',
  pending: 'warning',
  published: 'success',
};

/**
 * 상태에 따른 AppBadge variant 반환
 *
 * @param status - 영문 상태값
 * @returns AppBadge 컴포넌트에 전달할 variant
 *
 * @example
 * getStatusBadgeVariant('completed') // 'success'
 * getStatusBadgeVariant('generating') // 'secondary'
 */
export function getStatusBadgeVariant(status: string | undefined | null): BadgeVariant {
  if (!status) {return 'neutral';}
  return STATUS_BADGE_VARIANTS[status.toLowerCase()] || 'warning';
}

// ============================================================================
// 상태 아이콘 관련 함수
// ============================================================================

/**
 * 상태별 아이콘 컴포넌트 매핑
 */
const STATUS_ICONS = {
  completed: CheckCircle,
  generating: Zap,
  music_generating: Music,
  metadata_ready: FileCheck,
  paused: Pause,
  cancelled: Ban,
  running: Zap,
  in_progress: Zap,
  failed: XCircle,
  pending: Clock,
  published: Megaphone,
  default: AlertCircle,
  list: List,
} as const;

/**
 * 상태에 따른 아이콘 ReactNode 반환
 *
 * @param status - 영문 상태값
 * @param size - 아이콘 크기 ('sm' | 'md' | 'lg'), 기본값 'sm'
 * @returns 아이콘 ReactNode
 *
 * @example
 * getStatusIcon('completed') // <CheckCircle className="w-3.5 h-3.5" />
 * getStatusIcon('generating', 'md') // <Zap className="w-4 h-4" />
 */
export function getStatusIcon(
  status: string | undefined | null,
  size: IconSize = 'sm'
): ReactNode {
  const iconClass = ICON_SIZE_CLASSES[size];
  const normalizedStatus = status?.toLowerCase() || 'default';
  const IconComponent = STATUS_ICONS[normalizedStatus as keyof typeof STATUS_ICONS] || STATUS_ICONS.default;

  return createElement(IconComponent, { className: iconClass });
}

/**
 * 상태에 따른 아이콘 (List 스타일) 반환
 * RecentPlaylistsSection 등에서 사용하는 간략한 아이콘
 *
 * @param status - 영문 상태값
 * @param size - 아이콘 크기, 기본값 'sm'
 * @returns 아이콘 ReactNode
 */
export function getStatusListIcon(
  status: string | undefined | null,
  size: IconSize = 'sm'
): ReactNode {
  const iconClass = ICON_SIZE_CLASSES[size];
  const normalizedStatus = status?.toLowerCase();

  switch (normalizedStatus) {
    case 'completed':
      return createElement(CheckCircle, { className: iconClass });
    case 'generating':
    case 'running':
    case 'in_progress':
      return createElement(Zap, { className: iconClass });
    case 'music_generating':
      return createElement(Music, { className: iconClass });
    case 'metadata_ready':
      return createElement(FileCheck, { className: iconClass });
    case 'paused':
      return createElement(Pause, { className: iconClass });
    case 'cancelled':
      return createElement(Ban, { className: iconClass });
    case 'failed':
      return createElement(XCircle, { className: iconClass });
    default:
      return createElement(List, { className: iconClass });
  }
}
